import { Stack, Typography, Grid, Alert } from "@mui/material";
import Page from "common/components/Page";
import useState from "common/hooks/useState";
import LinearProgress from "@mui/material/LinearProgress";

import { Box } from "@mui/material";
import {
  DataThresholding,
  Event,
  InfoRounded,
  Token,
  TrackChanges,
} from "@mui/icons-material";

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress
          sx={{
            height: 10,
            borderRadius: 5,
            width: "100%",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#ff914c",
              borderRadius: 5,
            },
            "& .MuiLinearProgress-track": {
              backgroundColor: "grey",
            },
          }}
          variant="determinate"
          {...props}
        />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body1">{`${
          Math.round(props.value) < 10 ? "<10" : Math.round(props.value)
        }%`}</Typography>
      </Box>
    </Box>
  );
}

const Tile = ({ icon = <InfoRounded />, title, metric, progress, h1 }) => {
  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 1,
        width: { xs: "100%", sm: "auto" }, // Allow shrinking on mobile
      }}
    >
      <Stack spacing={2} alignItems="center">
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          {icon}
          <Typography
            variant="h4"
            fontWeight="bold"
            noWrap
            sx={{
              fontSize: {
                xs: "1rem",
                sm: "1.2rem",
                md: "1.5rem",
              },
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack sx={{ width: "80%" }} alignItems={metric ? "center" : "initial"}>
          {metric !== undefined ? (
            <Typography
              variant={h1 ? h1 : "body1"}
              noWrap
              sx={{
                fontSize: {
                  xs: h1 ? "2rem" : "0.875rem",
                  sm: h1 ? "2.4rem" : "1rem",
                  md: h1 ? "2.6rem" : "1.25rem",
                },
              }}
            >
              {metric}
            </Typography>
          ) : (
            <LinearProgressWithLabel
              value={Math.round(progress) < 10 ? 5 : progress}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const TileGrid = ({ error, tiles }) => {
  return (
    <Box sx={{ py: 8, px: 4 }}>
      <Grid
        container
        spacing={1}
        sx={{
          maxWidth: 1200,
          justifyContent: "center",
          mx: "auto",
          "& .MuiGrid-item": {
            "& > div": {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            },
          },
        }}
      >
        {error ? (
          <Grid item {...{ xs: 12, sm: 12, md: 9 }}>
            <Alert
              sx={{
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.5)",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                color: "#eee",
                fontSize: "18px !important",
              }}
              severity="error"
            >
              {error}
            </Alert>
          </Grid>
        ) : null}
        {tiles.map((tile, index) => (
          <Grid key={index} item {...tile.size}>
            <Tile {...tile} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default function Billing({ role, crew_name }) {
  const { data, loading, error, refresh } = useState("/api/billing");
  let errorMessage = null;
  if (data?.threshold >= 100) {
    errorMessage =
      "Your Echelone One instance is currently exceeding provisioned utilization capacity.";
  }
  if (data?.TenantCount > data?.maxTenants) {
    errorMessage =
      "Your Echelone One instance currently exceeds the provisioned tenant capacity.";
  }
  const tiles = [
    {
      icon: (
        <InfoRounded
          sx={{ height: "28px !important", width: "28px !important" }}
        />
      ),
      title: `${data?.customer} Instance`,
      metric: `Account Id: ${data?.account_id}`,
      size: { xs: 12, sm: 8, md: 5 },
    },
    {
      icon: (
        <Event sx={{ height: "28px !important", width: "28px !important" }} />
      ),
      title: "Agreement",
      metric: `Starting on ${data?.purchaseDate} for ${data?.duration}yr`,
      size: { xs: 12, sm: 4, md: 4 },
    },
    {
      icon: (
        <TrackChanges
          sx={{ height: "28px !important", width: "28px !important" }}
        />
      ),
      title: "Tenants",
      metric: `${data?.TenantCount || 0} / ${data?.maxTenants || 0}`,
      h1: `${data?.TenantCount || 0}`.length > 3 ? "h3" : "h2",
      size: { xs: 12, sm: 4, md: 4 },
    },
    {
      icon: (
        <Token sx={{ height: "28px !important", width: "28px !important" }} />
      ),
      title: "Echelon One Units",
      metric: data?.ecu || 0,
      h1: `${data?.ecu || 0}`.length > 3 ? "h3" : "h2",
      size: { xs: 12, sm: 8, md: 5 },
    },
    {
      icon: (
        <DataThresholding
          sx={{ height: "28px !important", width: "28px !important" }}
        />
      ),
      title: "Utilization Threshold",
      progress: Number(data?.threshold || 0),
      size: { xs: 12, sm: 12, md: 9 },
    },
  ];
  return (
    <Page refresh={refresh} header="Billing" loading={loading}>
      <TileGrid error={errorMessage} tiles={tiles} />
    </Page>
  );
}
